import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  MessageCircle, Check, X, Clock, Eye,
  ChevronDown, ChevronUp, Users, Filter,
  CreditCard, Send, Image as ImageIcon, Megaphone
} from "lucide-react";
import { toast } from "sonner";
import AdminTelegramBroadcast from "./AdminTelegramBroadcast";

const TABS = [
  { id: "models", label: "Por Modelo", icon: Users },
  { id: "methods", label: "Por Método de Pago", icon: CreditCard },
  { id: "broadcast", label: "Broadcast", icon: Megaphone },
];

const StatusBadge = ({ status }) => {
  const cfg = {
    pending: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: Clock, label: "Pendiente" },
    approved: { bg: "bg-green-500/20", text: "text-green-400", icon: Check, label: "Aprobada" },
    rejected: { bg: "bg-red-500/20", text: "text-red-400", icon: X, label: "Rechazada" },
  }[status] || { bg: "bg-white/10", text: "text-white/60", icon: Clock, label: status };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const RequestCard = ({ req, expanded, onToggle, onApprove, onReject, showCreator }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const tgLink = `https://t.me/${(req.telegramUsername || '').replace('@', '')}`;

  return (
    <div data-testid={`request-${req.id}`} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
      <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusBadge status={req.status} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-medium text-sm">@{req.telegramUsername}</span>
              {showCreator && <span className="text-white/40 text-xs">({req.creatorName})</span>}
            </div>
            <p className="text-white/40 text-xs mt-0.5">{formatDate(req.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <a
            href={tgLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`tg-btn-${req.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#229ED9]/20 text-[#229ED9] text-xs font-semibold hover:bg-[#229ED9]/30 transition-colors"
          >
            <Send className="w-3.5 h-3.5" /> Telegram
          </a>
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-4 space-y-4">
          <div>
            <Label className="text-white/50 text-xs mb-2 block flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5" /> Comprobante de pago
            </Label>
            <div className="bg-black rounded-lg overflow-hidden max-w-sm">
              <img src={req.paymentProof} alt="Comprobante" className="w-full h-auto max-h-80 object-contain" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <span className="text-white/40 text-xs">Método</span>
              <p className="text-white font-medium text-sm">{req.paymentMethod || '-'}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs">Tipo</span>
              <p className="text-white font-medium text-sm">{req.membershipType === 'monthly' ? 'Mensual' : 'Lifetime'}</p>
            </div>
            <div>
              <span className="text-white/40 text-xs">Precio</span>
              <p className="text-white font-medium text-sm">
                {req.priceUSD ? `$${req.priceUSD} USD` : '-'}
                {req.priceCOP ? <span className="text-white/50 text-xs ml-1">(${Number(req.priceCOP).toLocaleString('es-CO')} COP)</span> : ''}
              </p>
            </div>
          </div>

          {req.status === 'pending' && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={() => onApprove(req.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                <Check className="w-3.5 h-3.5 mr-1" /> Aprobar
              </Button>
              <Button size="sm" onClick={() => onReject(req.id)} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Rechazar
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminTelegramVip = () => {
  const [requests, setRequests] = useState([]);
  const [creators, setCreators] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedCreator, setSelectedCreator] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("");
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("models");
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchCreators(); fetchPaymentMethods(); fetchStats(); }, []);
  useEffect(() => { fetchRequests(); }, [filter, selectedCreator, selectedMethod]);

  const fetchCreators = async () => {
    try {
      const res = await fetch("/api/creators", { headers: getAuthHeaders() });
      if (res.ok) setCreators(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch("/api/settings/payment-methods");
      if (res.ok) {
        const data = await res.json();
        setPaymentMethods(data.methods || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (selectedCreator) params.set("creator", selectedCreator);
      if (selectedMethod) params.set("method", selectedMethod);
      const res = await fetch(`/api/admin/telegram-vip/requests?${params}`, { headers: getAuthHeaders() });
      if (res.ok) setRequests(await res.json());
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch("/api/admin/telegram-vip/stats", { headers: getAuthHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleAction = useCallback(async (requestId, action) => {
    try {
      const res = await fetch(`/api/admin/telegram-vip/requests/${requestId}/${action}`, {
        method: "PATCH", headers: getAuthHeaders()
      });
      if (res.ok) {
        if (action === 'approve') {
          const d = await res.json().catch(() => ({}));
          const auto = d?.autoSend || {};
          if (auto.status === 'sent') {
            toast.success("✅ Aprobada + link enviado por bot");
          } else if (auto.status === 'no_link_configured') {
            toast.warning("Aprobada — pero no hay link VIP configurado para esta modelo. Cargalo en el form.");
          } else if (auto.status === 'user_not_captured') {
            toast.warning("Aprobada — el usuario no activó el bot. Se lo tenés que enviar manual.");
          } else if (auto.status === 'failed') {
            toast.warning(`Aprobada — envío falló: ${auto.error || 'desconocido'}`);
          } else if (auto.status === 'bot_not_configured') {
            toast.warning("Aprobada — bot no configurado en el backend");
          } else {
            toast.success("Aprobada");
          }
        } else {
          toast.success("Rechazada");
        }
        fetchRequests(); fetchStats();
      }
    } catch { toast.error("Error"); }
  }, [getAuthHeaders]);

  // Group by creator
  const groupedByCreator = requests.reduce((acc, req) => {
    const key = req.creatorUsername || 'sin-modelo';
    if (!acc[key]) acc[key] = { name: req.creatorName || key, items: [] };
    acc[key].items.push(req);
    return acc;
  }, {});

  // Group by payment method
  const groupedByMethod = requests.reduce((acc, req) => {
    const key = req.paymentMethod || 'Sin método';
    if (!acc[key]) acc[key] = { items: [] };
    acc[key].items.push(req);
    return acc;
  }, {});

  const statusFilters = [
    { value: "pending", label: "Pendientes", cls: "yellow" },
    { value: "approved", label: "Aprobadas", cls: "green" },
    { value: "rejected", label: "Rechazadas", cls: "red" },
    { value: "", label: "Todas", cls: "white" }
  ];

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 data-testid="vip-page-title" className="text-2xl font-bold text-white mb-1">Telegram VIP</h1>
          <p className="text-white/50 text-sm">Comprobantes de pago</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: Clock, label: "Pendientes", val: stats.pending, color: "yellow" },
              { icon: Check, label: "Aprobadas", val: stats.approved, color: "green" },
              { icon: X, label: "Rechazadas", val: stats.rejected, color: "red" },
              { icon: Users, label: "Total", val: stats.total, color: "white" },
            ].map(s => (
              <div key={s.label} className="bg-white/5 rounded-xl border border-white/10 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <s.icon className={`w-4 h-4 text-${s.color}-400`} />
                  <span className="text-white/40 text-xs">{s.label}</span>
                </div>
                <p className={`text-xl font-bold text-${s.color}-400`}>{s.val}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-lg p-1 border border-white/10">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => { setActiveTab(t.id); setSelectedCreator(""); setSelectedMethod(""); }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === t.id ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/70'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Filters (oculto en Broadcast) */}
        {activeTab !== 'broadcast' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 flex-wrap flex-1">
            {statusFilters.map(f => (
              <button
                key={f.value}
                data-testid={`filter-${f.value || 'all'}`}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === f.value
                    ? `bg-${f.cls}-500/20 text-${f.cls}-400 border border-${f.cls}-500/30`
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Context filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
            {activeTab === 'models' ? (
              <select
                data-testid="creator-filter"
                value={selectedCreator}
                onChange={(e) => setSelectedCreator(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-lg pl-8 pr-8 py-1.5 text-xs appearance-none cursor-pointer hover:bg-white/10 transition-colors min-w-[160px]"
              >
                <option value="">Todas las modelos</option>
                {creators.map(c => <option key={c.username} value={c.username}>{c.name}</option>)}
              </select>
            ) : (
              <select
                data-testid="method-filter"
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-lg pl-8 pr-8 py-1.5 text-xs appearance-none cursor-pointer hover:bg-white/10 transition-colors min-w-[160px]"
              >
                <option value="">Todos los métodos</option>
                {paymentMethods.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            )}
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
          </div>
        </div>
        )}

        {/* Broadcast tab content */}
        {activeTab === 'broadcast' && <AdminTelegramBroadcast />}

        {/* Content (oculto en Broadcast) */}
        {activeTab !== 'broadcast' && (
        <div className="space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 text-white/15" />
              <p className="text-white/40 text-sm">No hay solicitudes</p>
            </div>
          ) : activeTab === 'models' ? (
            /* === VIEW: By Model === */
            Object.entries(groupedByCreator).map(([username, group]) => (
              <div key={username} data-testid={`creator-section-${username}`}>
                {!selectedCreator && (
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                    <h2 className="text-base font-bold text-white">{group.name}</h2>
                    <span className="text-white/30 text-xs">@{username}</span>
                    <span className="bg-white/10 text-white/50 text-xs px-2 py-0.5 rounded-full">{group.items.length}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {group.items.map(req => (
                    <RequestCard
                      key={req.id} req={req}
                      expanded={expandedRequest === req.id}
                      onToggle={() => setExpandedRequest(expandedRequest === req.id ? null : req.id)}
                      onApprove={(id) => handleAction(id, 'approve')}
                      onReject={(id) => handleAction(id, 'reject')}
                      showCreator={false}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            /* === VIEW: By Payment Method === */
            Object.entries(groupedByMethod).map(([method, group]) => (
              <div key={method} data-testid={`method-section-${method}`}>
                {!selectedMethod && (
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <CreditCard className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h2 className="text-base font-bold text-white">{method}</h2>
                    <span className="bg-white/10 text-white/50 text-xs px-2 py-0.5 rounded-full">{group.items.length}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {group.items.map(req => (
                    <RequestCard
                      key={req.id} req={req}
                      expanded={expandedRequest === req.id}
                      onToggle={() => setExpandedRequest(expandedRequest === req.id ? null : req.id)}
                      onApprove={(id) => handleAction(id, 'approve')}
                      onReject={(id) => handleAction(id, 'reject')}
                      showCreator={true}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTelegramVip;
